/**
 * Predictive Search - Matches screenshot spec
 * No external libraries
 */
(function() {
  'use strict';

  function sizedShopifyImageUrl(url, width) {
    if (!url) return '';
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}width=${width}`;
  }

  function debounce(fn, delay) {
    let t;
    return function(...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function formatMoney(val, format) {
    if (val == null) return '';
    let num = typeof val === 'string' ? parseFloat(val.replace(/[^0-9.-]/g, '')) : Number(val);
    if (isNaN(num)) return '';
    const value = num >= 100 && num === Math.floor(num) ? (num / 100).toFixed(2) : num.toFixed(2);
    const fmt = format || window.predictiveSearchMoneyFormat || '${{amount}}';
    return fmt.replace(/\{\{amount\}\}/g, value).replace(/\{\{amount_no_decimals\}\}/g, Math.round(num >= 100 ? num / 100 : num));
  }

  function buildProductUrlFromRoot(handle) {
    const root = (window.Shopify?.routes?.root || '/').replace(/\/+$/, '') || '';
    return root ? `${root}/products/${handle || ''}` : `/products/${handle || ''}`;
  }

  /**
   * Normalize Shopify /collections/x/products.json product for theme search UIs.
   */
  function normalizeProductForSearch(p) {
    const img = (p.featured_image && p.featured_image.src) || (p.images && p.images[0] && p.images[0].src) || '';
    const variants = p.variants || [];
    const v0 = variants[0] || {};
    const price = v0.price || p.price || '0';
    const url = buildProductUrlFromRoot(p.handle);
    const available =
      p.available !== false &&
      variants.length > 0 &&
      variants.some(v => v.available !== false);
    return {
      title: p.title,
      handle: p.handle,
      url,
      image: img,
      featured_image: img ? { url: img } : null,
      price,
      available,
      variants,
      body: (p.body_html || p.description || '').replace(/<[^>]+>/g, ''),
    };
  }

  function escapeRegExp(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Lower score = stronger match. Returns null if no match.
   * - Title / URL handle / variants (PS99 "Huge" often lives in options, not product title)
   * - Body uses whole-word match for short queries to avoid junk substring hits on other games
   */
  function productSearchMatchScore(p, q) {
    if (!q) return null;
    const title = (p.title || '').toLowerCase();
    const handle = (p.handle || '').toLowerCase();
    if (title.includes(q) || handle.includes(q)) return 0;

    const variants = p.variants || [];
    for (let i = 0; i < variants.length; i++) {
      const v = variants[i];
      const parts = [v.title, v.option1, v.option2, v.option3, v.sku].map(x => String(x || '').toLowerCase());
      if (parts.some(c => c.includes(q))) return 1;
    }

    const tagsRaw = p.tags;
    const tags = (Array.isArray(tagsRaw) ? tagsRaw.join(' ') : String(tagsRaw || '')).toLowerCase();
    if (tags.includes(q)) return 2;

    const type = String(p.product_type || '').toLowerCase();
    const vendor = String(p.vendor || '').toLowerCase();
    if (type.includes(q) || vendor.includes(q)) return 3;

    const body = ((p.body_html || p.description || '').replace(/<[^>]+>/g, '')).toLowerCase();
    if (body.length && q.length <= 48) {
      try {
        const re = new RegExp('\\b' + escapeRegExp(q) + '\\b', 'i');
        if (re.test(body)) return 4;
      } catch (e) {
        /* ignore invalid regex */
      }
    } else if (body.includes(q)) return 4;

    return null;
  }

  /**
   * Search only products that live in active game collections (header + homepage).
   * Used by predictive search popover and search modal — keep in sync with search-active-collection-handles.liquid.
   */
  async function activeCollectionProductSearch(query, options) {
    const limit = (options && options.limit) || 8;
    const q = String(query || '').toLowerCase().trim();
    const handles = window.predictiveSearchCollectionHandles || [];
    if (!handles.length || q.length < 2) return [];

    const root = window.Shopify?.routes?.root || '/';

    const fetches = handles.map(h =>
      fetch(`${root}collections/${encodeURIComponent(h)}/products.json?limit=250`, {
        headers: { Accept: 'application/json' },
      })
    );

    const responses = await Promise.all(fetches);
    const dataList = await Promise.all(responses.map(r => (r.ok ? r.json() : { products: [] })));

    const byHandle = new Map();
    dataList.forEach(data => {
      (data.products || []).forEach(p => {
        if (p && p.handle && !byHandle.has(p.handle)) byHandle.set(p.handle, p);
      });
    });

    const scored = Array.from(byHandle.values())
      .filter(p => p.available !== false && (!p.variants || p.variants.some(v => v.available !== false)))
      .map(p => {
        const score = productSearchMatchScore(p, q);
        return score === null ? null : { p, score };
      })
      .filter(Boolean)
      .sort((a, b) => a.score - b.score || (a.p.title || '').localeCompare(b.p.title || ''));

    return scored.slice(0, limit).map(({ p }) => normalizeProductForSearch(p));
  }

  window.activeCollectionProductSearch = activeCollectionProductSearch;

  const PredictiveSearch = {
    overlay: null,
    popover: null,
    input: null,
    results: null,
    empty: null,
    loading: null,
    preview: null,
    activeIndex: -1,
    products: [],
    isMobile: () => window.innerWidth < 768,

    init() {
      this.overlay = document.querySelector('[data-predictive-search-overlay]');
      this.popover = document.querySelector('[data-predictive-search]');
      this.input = document.querySelector('[data-predictive-search-input]');
      this.results = document.querySelector('[data-predictive-search-results]');
      this.empty = document.querySelector('[data-predictive-search-empty]');
      this.loading = document.querySelector('[data-predictive-search-loading]');
      this.preview = document.querySelector('[data-predictive-search-preview]');

      if (!this.popover || !this.input || !this.results) return;

      this.bindEvents();
    },

    bindEvents() {
      document.querySelectorAll('[data-search-trigger]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      if (this.input) {
        this.input.addEventListener('focus', () => this.open());
        this.input.addEventListener('input', debounce((e) => this.onInput(e.target.value), 250));
        this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      document.querySelectorAll('[data-predictive-search-close]').forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });
    },

    isOpen() {
      return this.overlay?.classList.contains('is-open');
    },

    open() {
      if (!this.overlay || !this.popover) return;
      this.overlay.classList.add('is-open');
      this.popover.removeAttribute('hidden');
      this.popover.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      setTimeout(() => this.input?.focus(), 50);
    },

    close() {
      if (!this.overlay || !this.popover) return;
      this.overlay.classList.remove('is-open');
      this.popover.setAttribute('hidden', '');
      this.popover.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    },

    onInput(q) {
      const qq = (q || '').trim();
      if (qq.length < 2) {
        this.clearResults();
        return;
      }
      this.search(qq);
    },

    clearResults() {
      this.products = [];
      this.activeIndex = -1;
      if (this.empty) {
        this.empty.style.display = 'block';
        this.empty.textContent = 'Start typing to search products';
      }
      if (this.loading) this.loading.style.display = 'none';
      this.renderResults([]);
      this.hidePreview();
      this.popover?.classList.remove('has-preview');
    },

    async search(query) {
      if (this.empty) this.empty.style.display = 'none';
      if (this.loading) this.loading.style.display = 'flex';
      this.renderResults([]);

      try {
        const products = await activeCollectionProductSearch(query, { limit: 8 });
        this.products = products;
        this.activeIndex = -1;
        if (this.loading) this.loading.style.display = 'none';

        if (products.length === 0) {
          if (this.empty) {
            this.empty.style.display = 'block';
            this.empty.textContent = `No products found for "${query}"`;
          }
          this.renderResults([]);
        } else {
          if (this.empty) this.empty.style.display = 'none';
          this.renderResults(products);
        }
      } catch (err) {
        console.error('Predictive search error:', err);
        if (this.loading) this.loading.style.display = 'none';
        if (this.empty) {
          this.empty.style.display = 'block';
          this.empty.textContent = 'Search failed. Please try again.';
        }
        this.renderResults([]);
      }
    },

    buildProductUrl(handle) {
      return buildProductUrlFromRoot(handle);
    },

    renderResults(products) {
      if (!this.results) return;

      const existing = this.results.querySelectorAll('.predictive-search__empty, .predictive-search__loading');
      existing.forEach(el => el.remove());

      const container = document.createElement('div');
      container.className = 'predictive-search__list';

      const bgUrl = window.predictiveSearchBgUrl || '';

      products.forEach((p, i) => {
        const imgRaw = (p.image || (p.featured_image && p.featured_image.url)) || '';
        const img = imgRaw ? sizedShopifyImageUrl(imgRaw, 160) : '';
        const price = p.price != null ? formatMoney(p.price) : '';
        const url = p.url || this.buildProductUrl(p.handle);

        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'predictive-search__result';
        row.setAttribute('role', 'option');
        row.setAttribute('id', `predictive-option-${i}`);
        row.setAttribute('data-index', String(i));
        row.setAttribute('data-url', url);

        row.innerHTML = `
          <div class="predictive-search__thumb-wrap">
            <img src="${img || bgUrl}" alt="${(p.title || '').replace(/"/g, '&quot;')}" width="80" height="80" loading="lazy" decoding="async">
          </div>
          <div class="predictive-search__result-info">
            <h4 class="predictive-search__result-title">${(p.title || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</h4>
            <p class="predictive-search__result-price">${price}</p>
            <div class="predictive-search__result-expand">
              <p class="predictive-search__result-expand-desc" data-expand-desc></p>
              <a href="${url}" class="predictive-search__result-expand-link" data-expand-link>View product</a>
            </div>
          </div>
        `;

        const descEl = row.querySelector('[data-expand-desc]');
        const body = (p.body || '').replace(/<[^>]+>/g, '').trim();
        if (descEl && body) descEl.textContent = body.slice(0, 120) + (body.length > 120 ? '...' : '');

        row.addEventListener('click', (e) => {
          if (e.target.closest('[data-expand-link]')) return;
          if (this.isMobile()) {
            e.preventDefault();
            window.location.href = url;
          } else {
            window.location.href = url;
          }
        });

        row.addEventListener('mouseenter', () => this.showPreview(p, i));
        row.addEventListener('mouseleave', () => this.hidePreview());

        row.addEventListener('focus', () => {
          this.activeIndex = i;
          this.updateActiveOption();
          this.showPreview(p, i);
        });

        container.appendChild(row);
      });

      const list = this.results.querySelector('.predictive-search__list');
      if (list) list.remove();
      this.popover?.classList.toggle('has-preview', products.length > 0 && !this.isMobile());
      this.results.appendChild(container);
      this.activeIndex = products.length > 0 ? 0 : -1;
      this.updateActiveOption();
      if (products.length > 0 && !this.isMobile()) {
        this.showPreview(products[0], 0);
      }
    },

    showPreview(product, index) {
      if (!this.preview || this.isMobile()) return;

      const imgRaw = product.image || (product.featured_image && product.featured_image.url) || '';
      const img = imgRaw ? sizedShopifyImageUrl(imgRaw, 400) : '';
      const price = product.price != null ? formatMoney(product.price) : '';
      const body = (product.body || '').replace(/<[^>]+>/g, '').trim();
      const desc = body.slice(0, 120) + (body.length > 120 ? '...' : '');
      const url = product.url || this.buildProductUrl(product.handle);

      const imgEl = this.preview.querySelector('[data-preview-image]');
      const titleEl = this.preview.querySelector('[data-preview-title]');
      const priceEl = this.preview.querySelector('[data-preview-price]');
      const descEl = this.preview.querySelector('[data-preview-desc]');
      const linkEl = this.preview.querySelector('[data-preview-link]');

      if (imgEl) imgEl.src = img;
      if (imgEl) imgEl.alt = product.title || '';
      if (titleEl) titleEl.textContent = product.title || '';
      if (priceEl) priceEl.textContent = price;
      if (descEl) descEl.textContent = desc;
      if (linkEl) linkEl.href = url;

      this.preview.removeAttribute('aria-hidden');
    },

    hidePreview() {
      if (this.preview) this.preview.setAttribute('aria-hidden', 'true');
    },

    updateActiveOption() {
      const options = this.results?.querySelectorAll('.predictive-search__result');
      options?.forEach((opt, i) => opt.classList.toggle('is-active', i === this.activeIndex));
      const active = options?.[this.activeIndex];
      this.popover?.setAttribute('aria-activedescendant', active ? active.id : '');
    },

    onKeydown(e) {
      if (!this.results) return;

      const options = Array.from(this.results.querySelectorAll('.predictive-search__result[role="option"]'));
      if (options.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, options.length - 1);
        this.updateActiveOption();
        options[this.activeIndex]?.focus();
        const p = this.products[this.activeIndex];
        if (p) this.showPreview(p, this.activeIndex);
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
        this.updateActiveOption();
        options[this.activeIndex]?.focus();
        const p = this.products[this.activeIndex];
        if (p) this.showPreview(p, this.activeIndex);
        return;
      }

      if (e.key === 'Enter') {
        const active = options[this.activeIndex];
        if (active) {
          e.preventDefault();
          const url = active.getAttribute('data-url');
          if (url) window.location.href = url;
        }
      }
    }
  };

  document.addEventListener('DOMContentLoaded', () => PredictiveSearch.init());
})();
